import { DataEvents, Diagram, locale, SelectionEvents } from "@dhx/ts-diagram";
import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { Property } from "./property-module/Property";
import { PropertyEvents } from "./property-module/types";
import { ISidebarConfig, DiagramEditorEvents } from "./types";

export class Sidebar {
	protected _config: ISidebarConfig;
	protected _events;

	private _ui: Property;
	private _diagram: Diagram;
	private _selectItem: string;

	constructor(diagram: Diagram, config?: ISidebarConfig, events?) {
		this._diagram = diagram;
		this._config = config;
		this._events = events;

		this._ui = new Property(null);
		this._ui.events.on(PropertyEvents.change, () => this.getValues());
		this._diagram.events.on(DataEvents.change, () => this.setValues());

		this.empty();

		this._diagram.events.on(SelectionEvents.afterSelect, () => {
			awaitRedraw().then(() => {
				this._updateData();
				this.setValues();
			});
		});
		this._events.on(DiagramEditorEvents.redoButton, () => {
			const itemConfig = this._diagram.selection.getItem();
			if (itemConfig) {
				this._updateData();
				this.setValues();
			} else {
				this._diagram.selection.remove();
				this.empty();
			}
		});
		this._events.on(DiagramEditorEvents.undoButton, () => {
			this._diagram.selection.remove();
			this.empty();
		});
		this._diagram.events.on(SelectionEvents.afterUnSelect, () => {
			this.empty();
		});
		this._ui.getRootView().hooks = {
			didRedraw: () => {
				if (this._selectItem) {
					this.select(this._selectItem);
					this._selectItem = null;
				}
			},
		};
	}
	setValues(): void {
		const config = this._diagram.selection.getItem();
		if (!config) return;

		const {
			backArrow,
			forwardArrow,
			connectType,
			cornersRadius,
			horizontalAlign,
			verticalAlign,
			width,
			height,
			angle,
			x,
			y,
			fill,
			stroke,
			strokeWidth,
			strokeType,
			fontColor,
			fontSize,
			lineHeight,
			fontStyle,
			fontWeight,
			textAlign,
			textVerticalAlign,
			dx,
			dy,
			headerColor,
			fromSide,
			toSide,
			style,
			header,
			subHeaderRows,
			subHeaderCols,
		} = config;

		const isOrgChart: boolean =
			this._diagram.config.type === "org" || this._diagram.config.type === "mindmap";
		const type = config.$item.getBaseType();

		if (type === "line" && config.$subSelected) {
			const commonConfig = config.title;
			const titleConfig = config.title.text.find(({ id }) => id === config.$subSelected);
			const values = {
				textPosition: {
					x: titleConfig.x,
					y: titleConfig.y,
				},
				fill: titleConfig.fill || commonConfig.fill || "",
				text: titleConfig.text,
				textProps: {
					fontColor: titleConfig.fontColor || commonConfig.fontColor,
					fontSize: titleConfig.fontSize || commonConfig.fontSize,
					lineHeight: titleConfig.lineHeight || commonConfig.lineHeight,
					fontStyle: titleConfig.fontStyle || commonConfig.fontStyle,
					fontWeight: titleConfig.fontWeight || commonConfig.fontWeight,
					textAlign: titleConfig.textAlign || commonConfig.textAlign,
				},
				gridStep: {
					step: this._diagram.config.gridStep,
				},
			};

			this._ui.setValues(values);
			return;
		}

		const connectorStroke =
			type === "line"
				? {
						backArrow: backArrow || "",
						forwardArrow: forwardArrow || "",
						connectType: connectType || "elbow",
						cornersRadius,
						fromSide,
						toSide,
				  }
				: {};

		let values = {
			...config,
			align: {
				horizontal: horizontalAlign || "",
				vertical: verticalAlign || "",
			},
			arrange: {
				x: x || 0,
				y: y || 0,
				width,
				height,
				angle: angle || 0,
			},
			arrangeGroup: {
				x: x || 0,
				y: y || 0,
				width,
				height,
			},
			fill: fill || "",
			strokeProps: {
				stroke: stroke || "#2196F3",
				strokeWidth,
				strokeType: strokeType || "line",
				...connectorStroke,
			},
			textProps: {
				fontColor,
				fontSize,
				lineHeight,
				fontStyle,
				fontWeight,
				textAlign,
				textVerticalAlign,
			},
			position: isOrgChart ? { dx: dx || 0, dy: dy || 0 } : { dx: x || 0, dy: y || 0 },
			size: {
				width,
				height,
			},
			headerColor: headerColor || "",
			gridStep: {
				step: this._diagram.config.gridStep,
			},
		};

		if (type === "swimlane" || type === "group") {
			values = {
				...values,

				fillGroup: style?.fill || "",
				arrangeGroup: {
					x: x || 0,
					y: y || 0,
					width,
					height,
				},
				strokeGroup: {
					stroke: style?.stroke,
					strokeWidth: style?.strokeWidth,
				},
				headerStyle: {
					fontColor: header?.fontColor,
					fontSize: header?.fontSize,
					lineHeight: header?.lineHeight,
					fontStyle: header?.fontStyle,
					fontWeight: header?.fontWeight,
					textAlign: header?.textAlign,
					textVerticalAlign: header?.textVerticalAlign,
				},
				headerText: header?.text,
				headerPosition: header?.position,
				headerEnable: header?.enable.toString(),
				headerFill: header?.fill || "",
			};
			if (type === "swimlane") {
				values = {
					...values,

					subHeaderRowsEnable: subHeaderRows?.enable?.toString(),
					subHeaderColsEnable: subHeaderCols?.enable?.toString(),
				};
			}
		}
		this._ui.setValues(values);
	}
	getValues(): void {
		const isOrgChart: boolean =
			this._diagram.config.type === "org" || this._diagram.config.type === "mindmap";
		const item = this._diagram.selection.getItem();
		const type = item?.$item.getBaseType();

		if (type === "line" && item.$subSelected) {
			let values = {
				...this._ui.getValues(),
			};

			if (values.textPosition) {
				values.x = values.textPosition.x * 1;
				values.y = values.textPosition.y * 1;

				delete values.textPosition;
			}
			if (values.textProps) {
				values = { ...values, ...values.textProps };

				delete values.textProps;
			}

			const titles = item.title.text.map(title => {
				if (title.id === item.$subSelected) {
					return {
						...title,
						...values,
					};
				}
				return title;
			});

			this._diagram.data.update(item.id, {
				title: {
					...item.title,
					text: titles,
				},
			});
			return;
		}

		let values = {
			...item,
			...this._ui.getValues(),
		};
		let isNeedUpdate = false;

		if (isOrgChart && values.position) {
			values.dx = values.position.dx * 1;
			values.dy = values.position.dy * 1;

			delete values.position;
		} else if (values.position) {
			values.x = values.position.dx * 1;
			values.y = values.position.dy * 1;

			delete values.position;
		}
		if (values.size) {
			values.width = values.size.width * 1;
			values.height = values.size.height * 1;

			delete values.size;
		}
		if (values.arrange) {
			values.x = values.arrange.x * 1;
			values.y = values.arrange.y * 1;

			values.width = values.arrange.width * 1;
			values.height = values.arrange.height * 1;
			values.angle = values.arrange.angle * 1;

			delete values.arrange;
		}
		if (values.arrangeGroup) {
			values.x = values.arrangeGroup.x * 1;
			values.y = values.arrangeGroup.y * 1;

			values.width = values.arrangeGroup.width * 1;
			values.height = values.arrangeGroup.height * 1;

			delete values.arrangeGroup;
		}
		if (values.align) {
			values.horizontalAlign = values.align.horizontal;
			values.verticalAlign = values.align.vertical;
			if (values.align.vertical === "bottom") {
				values.dy = values.height;
			}
			if (values.align.vertical === "top") {
				values.dy = -values.height;
			}
			if (values.align.vertical === "middle") {
				values.dy = 0;
			}
			if (values.align.horizontal === "center") {
				values.dx = 0;
			}
			if (values.align.horizontal === "left") {
				values.dx = -values.width;
			}
			if (values.align.horizontal === "right") {
				values.dx = values.width;
			}
		}
		if (values.strokeProps) {
			if (values.connectType !== values.strokeProps.connectType) {
				values.points = null;
			}
			values = { ...values, ...values.strokeProps };
			delete values.strokeProps;
		}
		if (values.textProps) {
			values = { ...values, ...values.textProps };
			delete values.textProps;
		}
		if (values.gridStep) {
			const value = parseFloat(values.gridStep.step);
			if (value > 0) {
				this._events.fire(DiagramEditorEvents.changeGridStep, [value]);
			}
			delete values.gridStep;
		}
		if (values.fillGroup) {
			values.style.fill = values.fillGroup;

			delete values.fillGroup;
		}
		if (values.strokeGroup) {
			values.style = { ...values.style, ...values.strokeGroup };

			delete values.strokeGroup;
		}
		if (values.headerStyle) {
			values.header = { ...values.header, ...values.headerStyle };

			delete values.headerStyle;
		}
		if (values.headerText) {
			values.header.text = values.headerText;

			delete values.headerText;
		}
		if (values.headerPosition) {
			values.header.position = values.headerPosition;

			delete values.headerPosition;
		}
		if (values.headerEnable) {
			const isBool = values.headerEnable === "false" ? false : true;
			if (values.header.enable !== isBool && values.header) {
				if (type === "swimlane") {
					const pull = {};
					const headerPosition = values.header.position;

					for (let i = 0; i < item.layout.length; i++) {
						const row = item.layout[i];
						for (let k = 0; k < row.length; k++) {
							const cell = row[k];

							if (!pull[cell]) {
								this._diagram.data.eachChild(cell, child => {
									if (headerPosition === "top" || headerPosition === "bottom") {
										const offsetY = Math.round(
											values.header.height /
												((headerPosition === "top"
													? i
													: item.layout.length - (i + 1)) +
													1)
										);
										child.y = isBool ? child.y + offsetY : child.y - offsetY;
									} else {
										const offsetX = Math.round(
											values.header.height /
												((headerPosition === "left" ? k : row.length - (k + 1)) + 1)
										);
										child.x = isBool ? child.x + offsetX : child.x - offsetX;
									}
								});
								pull[cell] = true;
							}
						}
					}
				}
				isNeedUpdate = true;
			}
			values.header.enable = isBool;

			delete values.headerEnable;
		}
		if (values.subHeaderRowsEnable) {
			const isBool = values.subHeaderRowsEnable === "false" ? false : true;

			if (values.subHeaderRows.enable !== isBool && values.subHeaderRows) {
				const pull = {};
				const headerPosition = values.subHeaderRows.position;

				for (let i = 0; i < item.layout.length; i++) {
					const row = item.layout[i];
					for (let k = 0; k < row.length; k++) {
						const cell = row[k];
						const offsetX = Math.round(
							values.header.height /
								((headerPosition === "left" ? k : row.length - (k + 1)) + 1)
						);

						if (!pull[cell]) {
							this._diagram.data.eachChild(cell, child => {
								child.x = isBool ? child.x + offsetX : child.x - offsetX;
							});
							pull[cell] = true;
						}
					}
				}
				isNeedUpdate = true;
			}
			values.subHeaderRows.enable = isBool;

			delete values.subHeaderRowsEnable;
		}
		if (values.subHeaderColsEnable) {
			const isBool = values.subHeaderColsEnable === "false" ? false : true;

			if (values.subHeaderCols.enable !== isBool && values.subHeaderCols) {
				const pull = {};
				const headerPosition = values.subHeaderCols.position;

				for (let i = 0; i < item.layout.length; i++) {
					const row = item.layout[i];
					for (let k = 0; k < row.length; k++) {
						const cell = row[k];
						const offsetY = Math.round(
							values.header.height /
								((headerPosition === "top" ? i : item.layout.length - (i + 1)) + 1)
						);

						if (!pull[cell]) {
							this._diagram.data.eachChild(cell, child => {
								child.y = isBool ? child.y + offsetY : child.y - offsetY;
							});
							pull[cell] = true;
						}
					}
				}
				isNeedUpdate = true;
			}
			values.subHeaderCols.enable = isBool;

			delete values.subHeaderColsEnable;
		}
		if (values.headerFill) {
			values.header.fill = values.headerFill;

			delete values.headerFill;
		}

		if (item) {
			this._diagram.data.update(item.id, values);

			if (isNeedUpdate) {
				this._updateData();
				this.setValues();
				this.paint();
			}
		}
	}
	clear(): void {
		this._ui.clear();
	}
	empty(): void {
		if (this._config.showGridStep) {
			this._ui.data.parse([
				{
					id: "gridStep",
					type: "inputsGroup",
					label: locale.gridStep,
					validate: "number",
					options: [
						{
							id: "step",
							value: this._diagram.config.gridStep,
							icon: () => el(".dxi.dxi-grid-step"),
							validate: "number",
						},
					],
				},
			]);
		} else {
			this._ui.data.parse([{ type: "empty" }]);
		}
	}
	select(id: string): void {
		this._ui.selectItem(id);
	}
	isItemsSelected(): boolean {
		return this._ui.isItemsSelected();
	}
	getUI(): any {
		return this._ui;
	}
	paint(): void {
		this._ui.paint();
	}
	private _updateData() {
		const itemConfig = this._diagram.selection.getItem();
		const item =
			itemConfig.$subSelected && itemConfig.$item.getBaseType() === "line"
				? itemConfig.title.text.find(({ id }) => id === itemConfig.$subSelected).$item
				: itemConfig.$item;
		const metaData = item.getMetaInfo().filter(control => {
			if (control.id === "gridStep" && !this._config.showGridStep) {
				return null;
			}
			return control;
		});
		this._ui.data.parse(metaData);
	}
}
